import { compileMerchantPatterns, SEED_MERCHANTS } from './registry.js';
import { normalizeDigits } from '../money.js';
/**
 * Noise that payment terminals bolt onto merchant descriptors. Removing it is
 * what turns "CARREFOUR MIRDIF DUBAI AE POS 4521" into "carrefour".
 */
const NOISE_TOKENS = new Set([
    'pos', 'posdebit', 'purchase', 'payment', 'pmt', 'txn', 'trn', 'ref', 'card',
    'visa', 'mastercard', 'mc', 'debit', 'credit', 'ecom', 'ecommerce', 'online',
    'llc', 'lLC', 'fzc', 'fze', 'fzco', 'ltd', 'limited', 'inc', 'co', 'trading',
    'general', 'est', 'establishment', 'br', 'branch', 'store', 'stores', 'shop',
    'intl', 'international', 'the', 'and', 'contactless', 'apple', 'pay', 'gpay',
    'applepay', 'googlepay', 'samsungpay', 'nfc', 'chip', 'pin',
]);
/** Country/city tokens we lift out as `location` rather than merchant identity. */
const LOCATION_TOKENS = new Set([
    'ae', 'uae', 'are', 'dubai', 'dxb', 'abudhabi', 'abu', 'dhabi', 'auh', 'sharjah',
    'ajman', 'fujairah', 'rak', 'rasalkhaimah', 'ummalquwain', 'alain', 'mirdif',
    'jbr', 'jlt', 'difc', 'marina', 'downtown', 'deira', 'barsha', 'jumeirah',
    'sa', 'ksa', 'riyadh', 'jeddah', 'gb', 'uk', 'london', 'us', 'usa', 'ny',
    'in', 'india', 'eg', 'egypt', 'qa', 'doha', 'kw', 'kuwait', 'bh', 'om',
]);
let compiledCache = compileMerchantPatterns(SEED_MERCHANTS);
/**
 * Replace the active merchant pattern set. The API calls this at boot with
 * seed records plus any merchants learned into the database.
 */
export function setMerchantRegistry(records) {
    compiledCache = compileMerchantPatterns(records);
}
export function getMerchantRegistry() {
    return compiledCache;
}
/**
 * Payment aggregators that prefix the descriptor with their own name, as
 * "PROCESSOR*ACTUAL MERCHANT". The half that identifies where the money went is
 * the one after the star, so the prefix is dropped.
 */
const PROCESSOR_PREFIXES = new Set([
    'paymob', 'tlr', 'fatoora', 'qlub', 'tap', 'telr', 'payby', 'ziina', 'stripe',
    'sq', 'sqsp', 'pp', 'paypal', 'ecom', 'wl', 'checkout', 'network',
]);
/**
 * Splits "PROCESSOR*MERCHANT" descriptors.
 *
 * The star cuts both ways and the meaningful half is not always the same one:
 * "Paymob*SHIPLIFIER INFO" names the processor first, while "FACEBK *MKXM2VVKE2"
 * names the merchant first and follows it with an opaque transaction id. So we
 * drop a known processor prefix, and otherwise drop a suffix that looks like a
 * reference rather than a name.
 */
function stripProcessorPrefix(raw) {
    const star = raw.indexOf('*');
    if (star <= 0)
        return raw;
    const head = raw.slice(0, star).trim();
    const tail = raw.slice(star + 1).trim();
    if (!head || !tail)
        return raw;
    if (PROCESSOR_PREFIXES.has(head.toLowerCase().replace(/[^a-z0-9]/g, '')))
        return tail;
    if (looksLikeReference(tail))
        return head;
    return raw;
}
/** An opaque token: letters and digits mixed, no separate words, not a name. */
function looksLikeReference(s) {
    const t = s.split(/[\s,]/)[0] ?? '';
    return t.length >= 6 && /[A-Za-z]/.test(t) && /\d/.test(t) && /^[A-Za-z0-9]+$/.test(t);
}
/** Strip diacritics and Arabic tatweel, fold case, collapse whitespace. */
export function cleanMerchantString(raw) {
    return normalizeDigits(stripProcessorPrefix(raw))
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/ـ/g, '')
        .replace(/[*#@_/\\|]+/g, ' ')
        .replace(/[^\p{L}\p{N}\s&.'+%-]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Turn a raw merchant descriptor into a stable identity.
 *
 * Order matters: a registry match is authoritative, an explicit user/DB alias
 * comes next, and only then do we fall back to token heuristics.
 */
export function normalizeMerchant(raw, aliases = new Map()) {
    const empty = {
        normalized: '', display: '', record: null, location: null, confidence: 0, method: 'heuristic',
    };
    if (!raw)
        return empty;
    const cleaned = cleanMerchantString(raw);
    if (!cleaned)
        return empty;
    // 1. Registry match against the *cleaned* string (patterns tolerate noise).
    for (const { merchant, regex } of compiledCache) {
        if (regex.test(cleaned)) {
            return {
                normalized: merchant.normalizedName,
                display: merchant.displayName,
                record: merchant,
                location: extractLocation(cleaned),
                confidence: 0.95,
                method: 'registry',
            };
        }
    }
    // 2. Learned alias (exact match on the cleaned lowercase string).
    const aliasKey = cleaned.toLowerCase();
    const aliased = aliases.get(aliasKey);
    if (aliased) {
        return {
            normalized: aliased,
            display: titleCase(aliased.replace(/_/g, ' ')),
            record: null,
            location: extractLocation(cleaned),
            confidence: 0.85,
            method: 'alias',
        };
    }
    // 3. Heuristic: drop noise + location tokens, keep the identity core.
    const tokens = aliasKey.split(/\s+/).filter(Boolean);
    const location = extractLocation(cleaned);
    const meaningful = tokens.filter((t) => {
        if (NOISE_TOKENS.has(t))
            return false;
        if (LOCATION_TOKENS.has(t))
            return false;
        // Store numbers, hotline numbers, and the "+971..." that some descriptors
        // carry where the city would normally be.
        if (/^\+?\d+$/.test(t))
            return false;
        if (/^[a-z]\d+$/.test(t))
            return false; // terminal ids like "t12"
        return true;
    });
    const core = meaningful.filter((t) => t.length > 1);
    // Falling all the way back to `tokens` would put the city into the identity,
    // so a one-or-two-letter name like "R AND B,DUBAI" became "r_and_b_dubai" and
    // never matched the same shop in another emirate. Drop the length rule first,
    // and only then give up.
    const identity = (core.length ? core : meaningful.length ? meaningful : tokens).slice(0, 4);
    const normalized = identity.join('_').replace(/[^a-z0-9_؀-ۿ]/g, '');
    return {
        normalized: normalized || aliasKey.replace(/\s+/g, '_'),
        display: titleCase(identity.join(' ')) || raw.trim(),
        record: null,
        location,
        // Heuristics are genuinely uncertain — say so rather than inflating.
        confidence: core.length ? 0.6 : 0.4,
        method: 'heuristic',
    };
}
function extractLocation(cleaned) {
    const tokens = cleaned.toLowerCase().split(/\s+/);
    const found = tokens.filter((t) => LOCATION_TOKENS.has(t) && t.length > 2);
    if (!found.length)
        return null;
    return titleCase(found[found.length - 1]);
}
export function titleCase(s) {
    return s
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => (w.length <= 3 && w === w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
        .join(' ');
}
/**
 * Similarity between two merchant strings, used to group near-identical
 * descriptors that no pattern covers (0..1).
 */
export function merchantSimilarity(a, b) {
    const x = cleanMerchantString(a).toLowerCase();
    const y = cleanMerchantString(b).toLowerCase();
    if (!x || !y)
        return 0;
    if (x === y)
        return 1;
    const setA = new Set(bigrams(x));
    const setB = new Set(bigrams(y));
    if (!setA.size || !setB.size)
        return 0;
    let shared = 0;
    for (const g of setA)
        if (setB.has(g))
            shared++;
    return (2 * shared) / (setA.size + setB.size);
}
function bigrams(s) {
    const out = [];
    for (let i = 0; i < s.length - 1; i++)
        out.push(s.slice(i, i + 2));
    return out;
}
//# sourceMappingURL=normalize.js.map